import { ICoin } from 'api/types/_coin';
import { Option } from 'api/types/_public';
import BreadCrumb from 'components/Common/BreadCrumb';
import FilterCoins from 'components/Common/FilterCoins';
import LabelWrapper from 'components/Common/LabelWrapper';
import TableContainer from 'components/Common/TableContainer';
import { useRole } from 'components/Hooks/UserHooks';
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { formatNumberWithCommas } from 'helpers/format';
import { ROLES_FOR_APP, isHavePermissionRole } from 'helpers/role';
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { getAllCoins, getCoins as onGetCoins } from "../../../store/thunks";


const TYPE_SELECT_DEFAULT: string = 'opening_price';
registerLocale('en', en);
registerLocale('ko', ko);

const CoinKeyword = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 200),
    sort_by: withDefault(StringParam, TYPE_SELECT_DEFAULT),
    order_by: withDefault(StringParam, `DESC`),
    market: withDefault(StringParam, ''),
    candle_date_time_utc: withDefault(StringParam, `${moment().format("Y-MM-DD")}`)
  });

  const rangeValue = useRef<any>();
  const [dateSearch, setDateSearch] = useState<string>(query?.candle_date_time_utc || `${moment().format("Y-MM-DD")}`);

  const [marketSearch, setMarketSearch] = useState<Option | null>(null);
  const [listCoin, setListCoin] = useState<ICoin[]>([]);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.Coin;

  const CoinProperties = createSelector(
    selectLayoutState,
    (state) => ({
      coins: state.coins,
      isCoinSuccess: state.isCoinSuccess,
      isCoinLoading: state.isCoinLoading,
      error: state.error,
    })
  );

  // Inside your component
  const { coins, isCoinLoading } = useSelector(CoinProperties);

  useEffect(() => {
    dispatch(onGetCoins(
      query.sort_by && rangeValue.current ?
        { ...query, [query.sort_by]: rangeValue.current?.value } : query
    ));
  }, [dispatch, query]);

  const searchData = () => {
    setQuery({
      ...query,
      market: marketSearch?.value ?? undefined,
      sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      candle_date_time_utc: dateSearch,
      time_request: + new Date()
    });
  };

  const resetData = () => {
    setQuery({
      title: undefined,
      sort_by: undefined,
      market: undefined,
      time_request: + new Date()
    }, "push")
    setMarketSearch(null);
    setDateSearch(`${moment().format("Y-MM-DD")}`);
  };


  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("Date"),
        accessor: "candle_date_time_kst",
        filterable: false,
        sortable: true,
        thWidth: 130,
        thClass: "text-start",
        Cell: (cell: any) => {
          const arrDate = String(cell?.value || "").split(" ");
          return (
            <div className="text-start" style={{ minWidth: '120px' }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t('Coin'),
        accessor: "coin",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const coin = cell?.value;
          return (
            <>
              <div style={{ minWidth: '100px' }} className="d-flex justify-content-start align-items-center">
                <div>
                  <img src={coin?.image_url} loading="lazy" alt={coin?.label} style={{ width: '28px', height: '28px' }} className="me-2" />
                </div>
                <div className="d-flex flex-column">
                  <div>
                    {i18n?.language === 'en' ? coin?.english_name : coin?.korean_name}
                  </div>
                  <div className="text-muted fs-12">
                    {coin?.code}/KRW
                  </div>

                </div>
              </div>
            </>
          )
        },
      },
      // {
      //   Header: t('candle_acc_trade_price'),
      //   accessor: "candle_acc_trade_price",
      //   filterable: false,
      //   sortable: true,
      //   thClass: "text-end",
      //   Cell: (cell: any) => {
      //     return (
      //       <>
      //         <div className="text-end pe-3" style={{ minWidth: '100px' }}>
      //           {formatNumberWithCommas(cell?.value || 0)}
      //         </div>
      //       </>
      //     )
      //   },
      // },
      // {
      //   Header: t('candle_acc_trade_volume'),
      //   accessor: "candle_acc_trade_volume",
      //   filterable: false,
      //   sortable: true,
      //   thClass: "text-end",
      //   Cell: (cell: any) => {
      //     return (
      //       <>
      //         <div className="text-end pe-3" style={{ minWidth: '100px' }}>
      //           {formatNumberWithCommas(cell?.value || 0)}
      //         </div>
      //       </>
      //     )
      //   },
      // },
      {
        Header: t('prev_closing_price'),
        accessor: "prev_closing_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3 text-danger" style={{ minWidth: '140px' }}>
                <strong>{formatNumberWithCommas(cell?.value || 0)}</strong>
              </div>
            </>
          )
        },
      },
      {
        Header: t('opening_price'),
        accessor: "opening_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3" style={{ minWidth: '130px' }}>
                <strong>{formatNumberWithCommas(cell?.value || 0)}</strong>
              </div>
            </>
          )
        },
      },
      {
        Header: t('trade_price'),
        accessor: "trade_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3 text-primary" style={{ minWidth: '120px' }}>
                <strong>{formatNumberWithCommas(cell?.value || 0)}</strong>
              </div>
            </>
          )
        },
      },
      {
        Header: t('change_price'),
        accessor: "change_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3" style={{ minWidth: '110px' }}>
                {formatNumberWithCommas(cell?.value || 0)}
              </div>
            </>
          )
        },
      },
      {
        Header: t('change_rate'),
        accessor: "change_rate",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3" style={{ minWidth: '140px' }}>
                {cell?.value} %
              </div>
            </>
          )
        },
      },
      {
        Header: t('low_price'),
        accessor: "low_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3 " style={{ minWidth: '120px' }}>
                {formatNumberWithCommas(cell?.value || 0)}
                <span className="text-primary" style={{ fontSize: '10px' }}>▼</span>
              </div>
            </>
          )
        },
      },
      {
        Header: t('high_price'),
        accessor: "high_price",
        filterable: false,
        sortable: true,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3 " style={{ minWidth: '120px' }}>
                {formatNumberWithCommas(cell?.value || 0)}
                <span className="text-danger" style={{ fontSize: '10px' }}>▲</span>
              </div>
            </>
          )
        },
      },

      // {
      //   Header: t("candle_date_time_utc"),
      //   accessor: "candle_date_time_utc",
      //   filterable: false,
      //   sortable: false,
      //   thWidth: 150,
      //   thClass: "text-end",
      //   Cell: (cell: any) => {
      //     const arrDate = String(cell?.value || "").split(" ");
      //     return (
      //       <div className="text-end" style={{ minWidth: '150px' }}>
      //         <span>
      //           <span>{arrDate?.[0] || ""}</span> <br />{" "}
      //           <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
      //         </span>
      //       </div>
      //     );
      //   },
      // },
      // {
      //   Header: t('Updated at'),
      //   accessor: "updated_at",
      //   filterable: false,
      //   sortable: true,
      //   thWidth: 130,
      //   thClass: "text-end",
      //   Cell: (cell: any) => {
      //     const arrDate = String(cell?.value || "").split(" ");
      //     return (
      //       <div className="text-end me-3" style={{ minWidth: '100px' }}>
      //         <span>
      //           <span>{arrDate?.[0] || ""}</span> <br />{" "}
      //           <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
      //         </span>
      //       </div>
      //     );
      //   },
      // },

    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (value: Date | null) => {
    setDateSearch((_prev) => value ? moment(new Date(value)).format("Y-MM-DD") : '');
  }

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return ({ ..._prev, ...sortBy });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleCallAllOption = async () => {
    try {
      const [resCoins]: any = await Promise.all([getAllCoins()]);
      setListCoin((_prev) => resCoins?.data || []);
      // Begin::set value default for form filter
      const market = String(query?.market || '');
      const optionMarket = (resCoins?.data || [])?.find((item: any) => String(item?.code || '') === market);
      setMarketSearch((_prev) => optionMarket ? ({ value: String(optionMarket?.code), label: `${i18n?.language === 'en' ? optionMarket?.english_name : optionMarket?.korean_name}` }) : null);
      // End::set value default for form filter
    } catch (error: any) {
      return error;
    }
  };

  useEffect(() => {
    handleCallAllOption();
  }, []);

  useEffect(() => {
    if (!marketSearch?.value) {
      setMarketSearch((prev) => null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    document.title = `${t('Coin')} - ${t('Keyword')} | NewsHub`;
    document.body.classList.remove('vertical-sidebar-enable');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('Coin')} pageTitle={t('Keyword')} />
          <Row>
            {isHavePermissionRole(ROLES_FOR_APP.EXCHANGE_RATE_LIST, userPermissions) && (
              <Col lg={12}>
                <Card id="leadsList">
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center mb-2 mt-2">
                      <Col sm={6} lg={3} className="date-picker-wrapper-custom mt-2 mb-2 mb-sm-0">
                        <LabelWrapper label={t('version_date')} isShow={!!dateSearch}>
                          <DatePicker
                            className="form-control search"
                            placeholderText={`${t('version_date')}...`}
                            value={dateSearch || undefined}
                            selected={new Date(dateSearch) || undefined}
                            dateFormat="yyyy-MM-dd"
                            isClearable={false}
                            maxDate={new Date()}
                            locale={i18n?.language === 'ko' ? 'ko' : 'en'}
                            onChange={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col sm={6} lg={3} className='mt-2 '>
                        <LabelWrapper label={t('Coin')} isShow={!!marketSearch?.value}>
                          <FilterCoins
                            dataList={listCoin}
                            initialValue={marketSearch}
                            onChangeSelect={(event: any) => {
                              setMarketSearch((_prev) => event);
                            }}
                            isHasOptionAll={true}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col sm={12} lg={3} className="hstack gap-1 justify-content-sm-center justify-content-md-between mt-sm-4 mt-md-2">
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                            disabled={isCoinLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t('Button Search')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t('Button Reset')}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody className="pt-3">
                    <div>
                      <TableContainer
                        className="custom-header-css"
                        divClass="table-responsive table-card"
                        tableClass="align-middle"
                        theadClass="table-light"
                        columns={columns}
                        data={coins?.list?.length ? coins?.list : []}
                        customPageSize={query.limit}
                        customPageCount={Math.ceil(Number(coins?.pagination?.total) / Number(coins?.pagination?.limit))}
                        customPageIndex={query.page - 1}
                        totalRecords={coins?.pagination?.total}
                        handleChangePage={handleChangePage}
                        manualSorting={true}
                        sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                        handleChangeSorting={handleChangeSorting}
                        isLoading={isCoinLoading}
                      />
                    </div>
                    <ToastContainer closeButton={false} limit={1} />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CoinKeyword;
